import type { InputHTMLAttributes, ReactNode } from 'react';
import { Icon } from './Icon';

const inputClasses =
  'h-[58px] w-full rounded-[15px] border bg-white px-[18px] text-[16px] font-normal text-ink shadow-[0_1px_0_rgba(0,0,0,0.02)] outline-none transition-[border-color,box-shadow,background-color] placeholder:text-grey-500 hover:border-black/15 focus:border-red-500 focus:bg-white focus:ring-3 focus:ring-red-500/10';

function FieldMessage({ id, error, hint }: { id: string; error?: string; hint?: string }) {
  if (error) {
    return (
      <span
        id={`${id}-error`}
        role="alert"
        className="mt-2 text-[13px] leading-[1.45] font-normal text-red-500">
        {error}
      </span>
    );
  }
  if (!hint) return null;
  return (
    <span id={`${id}-hint`} className="mt-2 text-[13px] leading-[1.45] font-normal text-grey-600">
      {hint}
    </span>
  );
}

export function BusinessFormField({
  id,
  label,
  className,
  error,
  hint,
  ...inputProps
}: {
  id: string;
  label: string;
  className?: string;
  error?: string;
  hint?: string;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'className'>) {
  const descriptionId = error ? `${id}-error` : hint ? `${id}-hint` : undefined;

  return (
    <label
      htmlFor={id}
      className={`flex min-w-0 flex-col text-[14px] leading-[1.35] font-medium text-ink ${className ?? ''}`}>
      <span className="mb-2">{label}</span>
      <input
        id={id}
        required
        aria-invalid={Boolean(error)}
        aria-describedby={descriptionId}
        {...inputProps}
        className={`${inputClasses} ${error ? 'border-red-500' : 'border-black-6'}`}
      />
      <FieldMessage id={id} error={error} hint={hint} />
    </label>
  );
}

export function BusinessChoiceField({
  id,
  name,
  label,
  type,
  options,
  otherValue,
  otherName,
  otherSelected,
  error,
  className,
}: {
  id: string;
  name: string;
  label: string;
  type: 'radio' | 'checkbox';
  options: string[];
  otherValue?: string;
  otherName?: string;
  otherSelected?: boolean;
  error?: string;
  className?: string;
}) {
  const choices = otherValue ? [...options, otherValue] : options;

  return (
    <fieldset
      aria-invalid={Boolean(error)}
      aria-describedby={error ? `${id}-error` : undefined}
      className={`flex min-w-0 flex-col text-[14px] leading-[1.35] font-medium text-ink ${className ?? ''}`}>
      <legend className="mb-3">{label}</legend>
      <div className="flex flex-wrap gap-2">
        {choices.map((choice) => {
          const isOther = choice === otherValue;
          return (
            <label
              key={choice}
              className="cursor-pointer rounded-pill border border-black-6 bg-white px-4 py-2.5 text-[14px] font-normal text-ink transition-colors hover:border-black/15 has-[:checked]:border-red-500 has-[:checked]:bg-red-500 has-[:checked]:text-white has-[:focus-visible]:ring-3 has-[:focus-visible]:ring-red-500/10">
              <input type={type} name={name} value={choice} className="sr-only" />
              {isOther ? 'Other' : choice}
            </label>
          );
        })}
      </div>
      {otherValue && otherSelected && otherName && (
        <input
          id={`${id}-other`}
          name={otherName}
          aria-label={`${label}: other`}
          placeholder="Tell us more"
          maxLength={200}
          className={`${inputClasses} mt-3 border-black-6`}
        />
      )}
      <FieldMessage id={id} error={error} />
    </fieldset>
  );
}

export function BusinessFormSuccess({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-[580px] animate-[waitlist-success-in_500ms_cubic-bezier(0.22,1,0.36,1)_both] flex-col items-center justify-center text-center motion-reduce:animate-none">
      <div className="flex size-16 animate-[waitlist-success-ring_600ms_cubic-bezier(0.22,1,0.36,1)_150ms_both] items-center justify-center rounded-full bg-red-500 text-white shadow-[0_14px_35px_rgba(215,0,10,0.2)] motion-reduce:animate-none">
        <Icon
          name="check"
          tint
          className="size-7 animate-[waitlist-success-check_400ms_ease-out_350ms_both] motion-reduce:animate-none"
        />
      </div>
      <h2 className="mt-9 text-[36px] leading-[1.08] font-semibold tracking-[-0.035em] text-ink md:text-[46px]">
        {title}
      </h2>
      <p className="mt-6 max-w-md text-body leading-[1.6] text-grey-700">{children}</p>
    </div>
  );
}
